import React, { useEffect, useState, memo } from 'react';
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { FormWrapper } from './Wrappers';
import { apiGetCountries } from './Api';

type Option = { label: string, value: string }

// The country a foreign supplier is in. Visma stores it as a code — NO, FI — and asking a treasurer
// to know the codes invites a typo that Visma either rejects mid-save or, worse, accepts: the
// country goes to the bank with an international payment.
//
// The list is Visma's own, so every code offered is one Visma takes, and the names read as they do
// in Spiris. An Autocomplete rather than a plain select because there are ~250 of them: typing
// "norg" narrows it at once, which a dropdown that long cannot.
function CountrySelectRaw({value, onChange, id}: {
  value: string,
  onChange: (newValue: string) => void,
  id?: string,
}) {
  const [options, setOptions] = useState<Option[]>([])

  useEffect(() => {
    let live = true
    // Fetched on mount — the dialog only renders this for a foreign supplier, so a domestic one
    // never pays for the request.
    apiGetCountries()
      .then(data => { if (live) setOptions(data.countries.map(c => ({value: c.code, label: `${c.name} (${c.code})`}))) })
      .catch(() => { /* reported on the error bus by Api; the field just stays empty */ })
    return () => { live = false }
  }, [])

  // `value` can be set before the list arrives — it is derived from the invoice's IBAN — so fall
  // back to showing the bare code rather than appearing empty until the fetch lands.
  const selected = options.find(option => option.value === value)
    ?? (value ? {value, label: value} : null)
  return (
    <Autocomplete<Option>
      id={id}
      autoHighlight
      options={options}
      value={selected}
      isOptionEqualToValue={(option, chosen) => option.value === chosen.value}
      onChange={(_event, option) => onChange(option === null ? '' : option.value)}
      renderInput={(params) => <TextField {...params} error={value === ''} size="small"/>}
      renderOption={(props, option) => <Box component="li" {...props}>{option.label}</Box>}
    />
  )
}

const CountrySelect = memo(FormWrapper({Component: CountrySelectRaw}))

export default CountrySelect
